import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Heart, Star } from "lucide-react";
import { ServiceData } from "@/hooks/useServiceData";

interface ServiceCardProps {
  service: ServiceData;
}

export const ServiceCard = ({ service }: ServiceCardProps) => {
  return (
    <Card 
      id={service.id} 
      className="overflow-hidden shadow-card hover:shadow-playful transition-all duration-500 hover:scale-105 bg-white flex flex-col h-full"
    >
      {/* Image */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <img 
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
          loading="eager"
          decoding="async"
          onError={(e) => {
            console.warn('[ServiceCard] Image error for', service.title, service.image);
            e.currentTarget.src = '/placeholder.svg';
          }}
          onLoad={(e) => {
            e.currentTarget.classList.add('loaded');
            console.log('[ServiceCard] Image loaded:', service.title, service.image);
          }}
        />
        <div className="absolute top-4 right-4">
          <Badge variant="secondary" className="bg-white/90 text-primary font-semibold">
            {service.price}
          </Badge>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent h-20" />
      </div>

      <CardContent className="p-8 flex flex-col flex-1">
        <CardHeader className="p-0 mb-6">
          <CardTitle className="text-2xl font-display font-bold mb-2">
            {service.title}
          </CardTitle>
          <CardDescription className="text-base leading-relaxed">
            {service.description}
          </CardDescription>
        </CardHeader>

        <div className="flex-1">
          {/* Features */}
          <div className="mb-6">
            <h4 className="font-semibold mb-3 flex items-center">
              <Star className="w-4 h-4 mr-2 text-accent" />
              Inclus dans le service
            </h4>
            <div className="grid grid-cols-1 xs:grid-cols-2 gap-2">
              {service.features.map((feature, index) => (
                <div key={index} className="flex items-start text-sm">
                  <div className="w-2 h-2 bg-primary rounded-full mr-2 flex-shrink-0 mt-2" />
                  <span className="leading-relaxed">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Themes */}
          {service.themes.length > 0 && (
            <div className="mb-6">
              <h4 className="font-semibold mb-3 flex items-center">
                <Heart className="w-4 h-4 mr-2 text-fun" />
                Thèmes disponibles
              </h4>
              <div className="flex flex-wrap gap-2">
                {service.themes.map((theme, index) => (
                  <Badge key={index} variant="outline" className="text-xs">
                    {theme}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-auto">
          <Button variant="default" className="flex-1 min-h-[48px] text-base" asChild>
            <a href="#contact" className="flex items-center justify-center">
              <span className="xs:hidden">Réserver</span>
              <span className="hidden xs:inline">Demande de Réservation</span>
            </a>
          </Button>
          <Button variant="outline" className="flex-1 min-h-[48px] text-base" asChild>
            <a 
              href={service.id === "chateaux" ? "/chateaux" : service.id === "sumos" ? "/sumos" : "#"} 
              className="flex items-center justify-center"
            >
              Voir Plus
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};